import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Stethoscope, ShieldCheck, Database, AlertTriangle } from "lucide-react";

const STATE_COLORS = {
  resolved: "bg-emerald-100 text-emerald-800",
  not_present: "bg-gray-200 text-gray-700",
  ambiguous: "bg-indigo-100 text-indigo-800",
  verification_required: "bg-amber-100 text-amber-900",
  unknown: "bg-amber-100 text-amber-800",
  unresolved: "bg-rose-100 text-rose-800",
};
const Pill = ({ s }) => (
  <span data-testid="pi-state" className={`px-2 py-0.5 rounded text-xs font-semibold ${STATE_COLORS[s] || "bg-gray-100 text-gray-600"}`}>{s}</span>
);

export default function ProviderIntel() {
  const [carriers, setCarriers] = useState({});
  const [q, setQ] = useState({ plan_year: 2026, contract_id: "H7617", pbp: "006", segment_id: "000", npi: "" });
  const [res, setRes] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const loadMeta = async () => {
    const [c, s] = await Promise.all([
      api.get(`/admin/provider-intel/carriers`),
      api.get(`/admin/provider-intel/cache/stats`),
    ]);
    setCarriers(c.data || {});
    setStats(s.data);
  };
  useEffect(() => { loadMeta(); }, []); // eslint-disable-line

  const resolve = async () => {
    setLoading(true); setErr(""); setRes(null);
    try {
      const r = await api.get(`/admin/provider-intel/resolve`, { params: q });
      setRes(r.data);
      loadMeta();
    } catch (e) { setErr(e?.response?.data?.detail || "lookup failed"); }
    setLoading(false);
  };

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto" data-testid="provider-intel-page">
      <PageHeader title="Provider Network Intelligence QA" icon={Stethoscope}
        subtitle="Deterministic medical-provider network status from authoritative carrier FHIR (CMS-9115-F Plan-Net). No LLM inference. Not consumer provider search." />

      {/* Carriers */}
      <div className="mt-6 rounded-xl border-2 border-[#E5E5E0] bg-white p-4" data-testid="pi-carriers">
        <div className="text-sm font-bold text-[#0B192C]">Supported carrier sources</div>
        <div className="mt-2 grid sm:grid-cols-2 gap-2 text-sm">
          {Object.entries(carriers).map(([k, c]) => (
            <div key={k} className="rounded-lg bg-[#F8F8F5] p-3">
              <div className="font-semibold flex items-center gap-2">{c.name}
                <Badge className={c.serves_ma ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"}>{c.serves_ma ? "MA feed" : "non-MA feed"}</Badge></div>
              <div className="text-xs text-[#6B7280] mt-1 break-all">{c.base}</div>
              <div className="text-[11px] text-[#6B7280] mt-1">{c.note}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Resolver */}
      <div className="mt-6 rounded-xl border-2 border-[#E5E5E0] bg-white p-4" data-testid="pi-resolver">
        <div className="text-sm font-bold text-[#0B192C]">Resolve provider network status</div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-2">
          <Input data-testid="pi-year" value={q.plan_year} onChange={(e) => setQ({ ...q, plan_year: e.target.value })} placeholder="Year" />
          <Input data-testid="pi-contract" value={q.contract_id} onChange={(e) => setQ({ ...q, contract_id: e.target.value })} placeholder="H7617" />
          <Input data-testid="pi-pbp" value={q.pbp} onChange={(e) => setQ({ ...q, pbp: e.target.value })} placeholder="PBP 006" />
          <Input data-testid="pi-segment" value={q.segment_id} onChange={(e) => setQ({ ...q, segment_id: e.target.value })} placeholder="Seg 000" />
          <Input data-testid="pi-npi" value={q.npi} onChange={(e) => setQ({ ...q, npi: e.target.value })} placeholder="Provider NPI" />
        </div>
        <Button data-testid="pi-resolve-btn" className="mt-3" onClick={resolve} disabled={loading || !q.npi}>
          {loading ? "Resolving…" : "Resolve"}
        </Button>
        {err && <p className="mt-2 text-sm text-red-600" data-testid="pi-error">{err}</p>}
      </div>

      {res && (
        <div className="mt-6 rounded-xl border-2 border-[#E5E5E0] bg-white p-4 space-y-3" data-testid="pi-result">
          <div className="flex items-center gap-3 flex-wrap">
            <Pill s={res.state} />
            <span className="text-sm text-[#6B7280]">{res.reason || ""}</span>
            {res.cache && <span className="text-[11px] px-2 py-0.5 rounded bg-[#F8F8F5] border">cache {res.cache}</span>}
          </div>
          {res.state === "resolved" || res.state === "ambiguous" ? (
            <div className="grid sm:grid-cols-2 gap-2 text-sm" data-testid="pi-result-detail">
              <div>Provider: <b>{res.provider_name || res.provider_npi}</b></div>
              <div>Specialty: <b>{(res.specialty || []).join(", ") || "—"}</b></div>
              <div>Matched networks: <b>{(res.network_ids || []).length}</b></div>
              <div>In-network locations: <b>{res.location_count}</b></div>
              {(res.locations || []).map((l) => (
                <div key={l.id} className="sm:col-span-2 text-xs text-[#6B7280]">• {l.name || "—"} — {l.address || "—"}</div>
              ))}
            </div>
          ) : null}
          {res.nppes_identity && (
            <div className="text-xs text-[#6B7280]" data-testid="pi-nppes">
              NPPES identity: {res.nppes_identity.name || (res.nppes_identity.found === false ? "not found" : "—")} ({res.nppes_identity.enumeration_type || "—"}) — <span className="text-amber-700">identity only, not network evidence</span>
            </div>
          )}
          {/* Provenance */}
          <div className="rounded-lg bg-[#F8F8F5] p-3" data-testid="pi-provenance">
            <div className="text-xs font-bold text-[#0B192C] flex items-center gap-2"><ShieldCheck className="w-4 h-4" />Provenance envelope</div>
            <div className="mt-1 grid sm:grid-cols-2 gap-1 text-[11px] text-[#374151]">
              <div>Source: <b>{res.source || "—"}</b> ({res.source_endpoint_type})</div>
              <div>Plan identity: <b>{res.contract_id}-{res.pbp}-{res.segment_id}-{res.plan_year}</b></div>
              <div>Source plan id: <b>{res.source_plan_identifier || "—"}</b></div>
              <div>Provider NPI: <b>{res.provider_npi}</b></div>
              <div>Source vintage: <b>{res.source_vintage || "—"}</b></div>
              <div>Resolver: <b>{res.resolver_version}</b></div>
              <div className="sm:col-span-2">Retrieved: {res.retrieved_at}</div>
            </div>
          </div>
        </div>
      )}

      {/* Cache */}
      <div className="mt-6 rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="pi-cache">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm font-bold text-amber-900 flex items-center gap-2"><Database className="w-4 h-4" />Freshness-aware cache</div>
          <Button variant="outline" data-testid="pi-cache-invalidate" onClick={async () => { await api.post(`/admin/provider-intel/cache/invalidate`, null, { params: { plan_year: q.plan_year } }); loadMeta(); }}>Invalidate (PY{q.plan_year})</Button>
        </div>
        {stats && (
          <div className="mt-2 text-sm text-amber-900" data-testid="pi-cache-stats">
            Entries <b>{stats.entries}</b> · resolved {stats.by_state?.resolved} · not_present {stats.by_state?.not_present} · ambiguous {stats.by_state?.ambiguous} · verification_required {stats.by_state?.verification_required} · TTL {stats.ttl_hours}h
          </div>
        )}
        <p className="text-[11px] text-amber-800 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Live FHIR + bounded cache. A cached fact is never returned once its carrier source vintage changes or TTL expires. unknown/unresolved are never cached.</p>
      </div>
    </div>
  );
}
